import moment from 'moment';
import * as yup from 'yup';
import { IContactCollectionSurveyOptions } from '../../../models/Api';

// Phone number regex. Accepts a US/NANP number (the masked default) OR a free-form international
// number entered via MaskedInput passthrough, which always begins with `+`. The BE's libphonenumber
// is the source of truth for international validity; the FE only sanity-checks `+` followed by enough
// phone characters so we don't re-introduce the US-only gate that would reject `+55…` etc.
const phoneRegExp = /^(?:((\+1)?[\s-]?)?\(?[1-9]\d\d\)?[\s-]?[1-9]\d\d[\s-]?\d\d\d\d|\+[\d\s().-]{6,})$/;

// The mask and the calendar picker both emit zero-padded MM/DD/YYYY, so that is the only
// canonical stored format. Strict parsing still rejects genuinely invalid dates.
const birthdayFormat = 'MM/DD/YYYY';

const isValidBirthday = (value: string) => {
	const date = moment(value, birthdayFormat, true);
	// Compare at day granularity so a birthday of today is accepted.
	return (
		date.isValid() &&
		date.isBefore(moment().add(1, 'day').startOf('day')) &&
		date.isAfter(moment().subtract(120, 'years'))
	);
};

/**
 * Builds the yup schema fields for the respondent contact-info step from a survey's contact-collection
 * options. Shared by every public survey form that renders `ContactInfoStep`; compose the result into
 * the form's `yup.object().shape({ ... })` alongside any form-specific fields.
 */
export const buildContactInfoSchemaFields = (options: IContactCollectionSurveyOptions = {}) => ({
	email:
		options.contactEmailOptions?.collect && options.contactEmailOptions?.require
			? yup.string().trim().email('Invalid email').required('Email is required')
			: yup.string(),
	firstName:
		options.contactNameOptions?.collect && options.contactNameOptions?.require
			? yup.string().required('First name is required')
			: yup.string(),
	lastName:
		options.contactNameOptions?.collect && options.contactNameOptions?.require
			? yup.string().required('Last name is required')
			: yup.string(),
	phoneNumber:
		options.contactPhoneNumberOptions?.collect && options.contactPhoneNumberOptions?.require
			? yup.string().trim().matches(phoneRegExp, 'Must be a valid phone number').required('Phone is required')
			: yup
					.string()
					.optional()
					.trim()
					.matches(phoneRegExp, { excludeEmptyString: true, message: 'Must be a valid phone number' }),
	birthday:
		options.contactBirthdayOptions?.collect && options.contactBirthdayOptions?.require
			? yup
					.string()
					.required('Birthday is required')
					.test('valid-date', 'Please enter a valid date (MM/DD/YYYY)', value => !!value && isValidBirthday(value))
			: yup
					.string()
					.optional()
					.test('valid-date', 'Please enter a valid date (MM/DD/YYYY)', value => !value || isValidBirthday(value)),
	address1:
		options.contactAddressOptions?.collect && options.contactAddressOptions?.require
			? yup.string().required('Address line 1 is required')
			: yup.string().optional(),
	address2: yup.string().optional(),
	city:
		options.contactAddressOptions?.collect && options.contactAddressOptions?.require
			? yup.string().required('City is required')
			: yup.string().optional(),
	stateProvince:
		options.contactAddressOptions?.collect && options.contactAddressOptions?.require
			? yup.string().required('State/Province is required')
			: yup.string().optional(),
	postalCode:
		options.contactAddressOptions?.collect && options.contactAddressOptions?.require
			? yup.string().required('Postal Code is required')
			: yup.string().optional(),
	country:
		options.contactAddressOptions?.collect && options.contactAddressOptions?.require
			? yup.string().required('Country is required')
			: yup.string().optional(),
});
